import { FormRow } from 'framework/ui/layout';
import {
  CurrencyInput,
  DateInput,
  Dropdown,
  NumberInput,
  FormFieldsData,
  useFormContext,
  ClearAfterUnmountWrapper,
} from 'framework/form';
import { useCallback } from 'react';
import moment from 'moment';
import { DATE_FORMAT } from 'framework/utils';
import { Stack } from '@mui/material';
import { get } from 'lodash';
import { BillingType, BILLING_TYPE_LABELS, PROJECT_SCENARIO, sumFtePositionsCount } from 'domain/shared/newProject';

const BudgetForm = ({
  source,
  expectedStructureSource,
  isUpdate = false,
}: {
  source: string;
  expectedStructureSource: string;
  isUpdate?: boolean;
}) => {
  const { data, onChangeCallback } = useFormContext();
  const isBillingTypeDisabled =
    data?.project?.createDaVinciRedmine === false && Boolean(data?.project?.existingProject?.defaultBudgetId);

  const billingTypeOptions = Object.entries(BILLING_TYPE_LABELS).map(([type, label]) => ({
    value: type,
    label,
    disabled:
      (data?.project?.scenario === PROJECT_SCENARIO.FIXED_SCOPE &&
        [BillingType.PERIODIC_PREPAID, BillingType.PERIODIC_POSTPAID].includes(type as BillingType)) ||
      (data?.project?.scenario === PROJECT_SCENARIO.DEDICATED_TEAM && type === BillingType.MILESTONE),
  }));

  const isMilestoneBasedType = data?.budget?.billingType === BillingType.MILESTONE;

  const onOfficialStartDateChange = useCallback(
    (values: FormFieldsData, isFieldInitializing?: boolean) => {
      const startDate = values[`${source}.startDate`];
      const daysToSubtract = isMilestoneBasedType ? 14 : 0;
      const softStartDate = startDate
        ? moment(startDate).subtract(daysToSubtract, 'days').format(DATE_FORMAT.API_ONLY_DATE)
        : '';

      onChangeCallback?.(
        {
          [`${source}.startDate`]: startDate,
          [`${source}.softStartDate`]: softStartDate,
        },
        isFieldInitializing,
      );
    },
    [isMilestoneBasedType, onChangeCallback, source],
  );

  const onOfficialEndDateChange = useCallback(
    (values: FormFieldsData, isFieldInitializing?: boolean) => {
      const endDate = values[`${source}.endDate`];
      const daysToAdd = isMilestoneBasedType ? 14 : 7;
      const extendedEndDate = endDate ? moment(endDate).add(daysToAdd, 'days').format(DATE_FORMAT.API_ONLY_DATE) : '';
      onChangeCallback?.(
        {
          [`${source}.endDate`]: endDate,
          [`${source}.extendedEndDate`]: extendedEndDate,
        },
        isFieldInitializing,
      );
    },
    [isMilestoneBasedType, onChangeCallback, source],
  );

  const onBillingTypeChange = useCallback(
    (values: FormFieldsData, isFieldInitializing?: boolean) => {
      onChangeCallback?.(
        {
          [`${source}.billingType`]: values[`${source}.billingType`],
          [`${source}.startDate`]: '',
          [`${source}.softStartDate`]: '',
          [`${source}.endDate`]: '',
          [`${source}.extendedEndDate`]: '',
        },
        isFieldInitializing,
      );
    },
    [onChangeCallback, source],
  );

  return (
    <Stack>
      <FormRow colXl={3} colMd={3}>
        <Dropdown
          placeholder="Select options"
          options={billingTypeOptions}
          source={`${source}.billingType`}
          label="Billing type"
          disabled={isBillingTypeDisabled}
          onChangeCallback={onBillingTypeChange}
        />
        <CurrencyInput suffix={'USD'} label="SOW amount" source={`${source}.amount`} placeholder=" " />
        <NumberInput
          source={'fte'}
          label="Max.FTE, people"
          value={sumFtePositionsCount(get(data, expectedStructureSource))}
          disabled
        />
        <NumberInput label="SOW hours" source={`${source}.hours`} />
      </FormRow>
      <FormRow colXl={3} colMd={3}>
        <DateInput
          label="SOW official start date"
          source={`${source}.startDate`}
          onChangeCallback={onOfficialStartDateChange}
          disabled={isUpdate}
        />
        <DateInput label="SOW soft start date" source={`${source}.softStartDate`} disabled />
        <DateInput
          label="SOW official end date"
          source={`${source}.endDate`}
          onChangeCallback={onOfficialEndDateChange}
        />
        <DateInput label="SOW extended end date" source={`${source}.extendedEndDate`} disabled />
        <ClearAfterUnmountWrapper isVisible={!data?.sow?.docusignLink}>
          <DateInput source={`${source}.expectedSowSigningDate`} label="SOW planned signing date" />
        </ClearAfterUnmountWrapper>
      </FormRow>
    </Stack>
  );
};

export default BudgetForm;
